import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import React, {forwardRef, useContext, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from "react";
import ChatMessage from "./ChatMessage";
import PlatformBadge from "./PlatformBadge";
import {LoadingButton} from "@mui/lab";
import SendIcon from '@mui/icons-material/Send';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {Context} from "../../index";
import CloseIcon from '@mui/icons-material/Close';
import EmojiPicker from 'emoji-picker-react';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MessageTemplates from './MessageTemplates';
import {getAllBalances} from '../../http/userAPI';
import useSnackbar from "../../hooks/useSnackbar";
import MySnackbar from "../MySnackbar";


const ChatBlockBottom = ({chat, sendMessageHandler, openChatHandler, sendingMessage, blockedByAnotherAdmin, messageValue, onMessageChange, onInsertTemplate, availablePlatforms = [], selectedPlatform = null, onPlatformChange}) => {
    const {user} = useContext(Context);
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState([]); // {file, url, kind: 'image'|'file', name}
    const [mode, setMode] = useState(null); // 'image' | 'file' | null
    const [showEmoji, setShowEmoji] = useState(false);
    const snack = useSnackbar(false, "", "warning");

    const imageInputRef = useRef(null);
    const fileInputRef = useRef(null);

    // Sync with external message value if provided
    useEffect(() => {
        if (messageValue !== undefined) {
            setMessage(messageValue);
        }
    }, [messageValue]);

    // Cleanup object URLs
    useEffect(() => {
        return () => {
            attachments.forEach(a => a.url && URL.revokeObjectURL(a.url));
        };
    }, [attachments]);

    const handleSend = () => {
        const files = attachments.map(a => a.file);
        if (message.trim() === '' && files.length === 0) return;
        sendMessageHandler(message, files);
        setMessage('');
        // Keep previews visible during upload; clear after sending finishes
    };

    // After sending completes, clear attachments
    useEffect(() => {
        if (!sendingMessage && attachments.length > 0) {
            // slight defer to ensure UI updates cleanly
            const t = setTimeout(() => {
                attachments.forEach(a => a.url && URL.revokeObjectURL(a.url));
                setAttachments([]);
                setMode(null);
            }, 100);
            return () => clearTimeout(t);
        }
    }, [sendingMessage]);

    const addFiles = (filesList, kind) => {
        if (!filesList || filesList.length === 0) return;
        // mutually exclusive sets
        let next = [];
        if (mode && mode !== kind) {
            // clear old
            attachments.forEach(a => a.url && URL.revokeObjectURL(a.url));
            next = [];
        } else {
            next = [...attachments];
        }

        const incoming = Array.from(filesList).slice(0, 10);
        for (const f of incoming) {
            if (next.length >= 10) break;
            const url = kind === 'image' ? URL.createObjectURL(f) : null;
            next.push({file: f, url, kind, name: f.name});
        }
        setAttachments(next);
        setMode(kind);
    };

    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items || items.length === 0) return;
        const images = [];
        const files = [];
        for (const item of items) {
            if (item.kind !== 'file') continue;
            const file = item.getAsFile();
            if (!file) continue;
            if (item.type && item.type.startsWith('image/')) {
                images.push(file);
            } else {
                files.push(file);
            }
        }
        if (mode === 'image') {
            if (images.length > 0) {
                e.preventDefault();
                addFiles(images, 'image');
            } else if (files.length > 0) {
                e.preventDefault();
                snack.handleChange(true, "Сначала удалите фото, чтобы вставить файлы", "warning");
            }
            return;
        }
        if (mode === 'file') {
            if (files.length > 0) {
                e.preventDefault();
                addFiles(files, 'file');
            } else if (images.length > 0) {
                e.preventDefault();
                snack.handleChange(true, "Сначала удалите файлы, чтобы вставить фото", "warning");
            }
            return;
        }
        if (images.length > 0) {
            e.preventDefault();
            addFiles(images, 'image');
            return;
        }
        if (files.length > 0) {
            e.preventDefault();
            addFiles(files, 'file');
        }
    };

    const removeAttachment = (idx) => {
        setAttachments(prev => {
            const copy = [...prev];
            const [removed] = copy.splice(idx, 1);
            if (removed?.url) URL.revokeObjectURL(removed.url);
            if (copy.length === 0) setMode(null);
            return copy;
        });
    };

    if (chat.adminId === null || (blockedByAnotherAdmin && user.isOwner)) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" p={2}>
                <LoadingButton onClick={() => openChatHandler(chat)} variant="contained" color="primary">
                    Открыть чат {blockedByAnotherAdmin && user.isOwner && "(занят)"}
                </LoadingButton>
            </Box>
        );
    } else if (chat.adminId === user?.user?.id) {
            return (
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    borderTop: '1px solid #ddd',
                    backgroundColor: '#f9f9f9', // Мягкий фон
                    borderRadius: '0 0 8px 8px', // Закругленные углы снизу
                    boxShadow: '0 -2px 4px rgba(0,0,0,0.05)', // Легкая тень
                }}
            >
                <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                    {/* Attachments preview */}
                    {attachments.length > 0 && (
                        <Box sx={{
                            mb: 1,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            background: '#fff',
                            border: '1px solid #eee',
                            overflowX: 'scroll',
                            minWidth: '300px',
                            maxWidth: '400px'
                        }}>
                            <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                {attachments.map((a, idx) => (
                                    <Box key={idx} sx={{
                                        position: 'relative',
                                        width: 80,
                                        minWidth: 80,
                                        height: 80,
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        bgcolor: '#fafafa'
                                    }}>
                                        {a.kind === 'image' ? (
                                            <img src={a.url} alt={a.name}
                                                 style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
                                        ) : (
                                            <Typography variant="caption" sx={{px: 1, textAlign: 'center'}}>
                                                {a.name}
                                            </Typography>
                                        )}
                                        {/* remove button */}
                                        <IconButton size="small" onClick={() => removeAttachment(idx)} sx={{
                                            position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.8)'
                                        }}>
                                            <CloseIcon fontSize="small"/>
                                        </IconButton>
                                        {/* loading overlay */}
                                        {sendingMessage && (
                                            <Box sx={{
                                                position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                            }}>
                                                <CircularProgress size={24}/>
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Селектор платформы отправки */}
                    {availablePlatforms.length > 0 && (
                        <Box sx={{mb: 1, display: 'flex', alignItems: 'center', gap: 0.5}}>
                            <Typography variant="caption" sx={{color: '#5e35b1', letterSpacing: 0.2}}>Отправить через:</Typography>
                            {availablePlatforms.length === 1 ? (
                                <PlatformBadge platform={availablePlatforms[0].platform} withLabel size="medium"/>
                            ) : (
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={selectedPlatform}
                                    onChange={(_, val) => {
                                        if (val) onPlatformChange?.(val);
                                    }}
                                >
                                    {availablePlatforms.map((p) => (
                                        <ToggleButton key={p.platform} value={p.platform} sx={{py: 0, px: 0.75, minHeight: 24, lineHeight: 1}}>
                                            <PlatformBadge platform={p.platform} withLabel size="small"/>
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            )}
                        </Box>
                    )}

                    <Box sx={{position: 'relative'}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {/* Pickers */}
                            {(mode === 'image' || mode === null) && (
                                <>
                                    <Tooltip title="Прикрепить фото">
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            disabled={sendingMessage}
                                            onClick={() => imageInputRef.current?.click()}
                                            aria-label="attach photos"
                                        >
                                            <PhotoCameraIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <input
                                        ref={imageInputRef}
                                        hidden
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={(e) => {
                                            addFiles(e.target.files, 'image');
                                            // reset value to allow re-adding same file name
                                            e.target.value = '';
                                        }}
                                    />
                                </>
                            )}
                            {(mode === 'file' || mode === null) && (
                                <>
                                    <Tooltip title="Прикрепить файлы">
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            disabled={sendingMessage}
                                            onClick={() => fileInputRef.current?.click()}
                                            aria-label="attach files"
                                        >
                                            <AttachFileIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <input
                                        ref={fileInputRef}
                                        hidden
                                        type="file"
                                        accept="application/*"
                                        multiple
                                        onChange={(e) => {
                                            addFiles(e.target.files, 'file');
                                            e.target.value = '';
                                        }}
                                    />
                                </>
                            )}
                            <Tooltip title="Смайлы">
                                <IconButton
                                    color="primary"
                                    size="small"
                                    disabled={sendingMessage}
                                    onClick={() => setShowEmoji((v) => !v)}
                                    aria-label="emoji picker"
                                >
                                    <EmojiEmotionsIcon/>
                                </IconButton>
                            </Tooltip>
                            <TextField
                                fullWidth
                                label="Введите сообщение"
                                value={message}
                                onChange={(e) => {
                                    setMessage(e.target.value);
                                    onMessageChange?.(e.target.value);
                                }}
                                onPaste={handlePaste}
                                onKeyDown={(e) => {
                                    if ((e.shiftKey || e.ctrlKey) && e.key === 'Enter') {
                                        e.preventDefault();
                                        setMessage(message + '\n');
                                    } else if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSend();

                                    }
                                }}
                                multiline
                                minRows={1}
                                maxRows={4}
                                autoFocus
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                    },
                                }}
                            />
                            <LoadingButton
                                loading={sendingMessage}
                                onClick={handleSend}
                                variant="contained"
                                color="primary"
                                sx={{borderRadius: '8px', minWidth: '48px', height: '100%'}}
                            >
                                <SendIcon/>
                            </LoadingButton>
                        </Stack>
                        {/* Floating emoji panel */}
                        {showEmoji && (
                            <Box sx={{position: 'absolute', bottom: 56, left: 8, zIndex: 1300}}>
                                <ClickAwayListener onClickAway={() => setShowEmoji(false)}>
                                    <Box sx={{boxShadow: 3, borderRadius: 1, overflow: 'hidden'}}>
                                        <EmojiPicker
                                            onEmojiClick={(emojiData) => {
                                                setMessage((prev) => prev + (emojiData?.emoji || ''));
                                            }}
                                            autoFocusSearch={false}
                                        />
                                    </Box>
                                </ClickAwayListener>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5}}>
                        <MessageTemplates
                            messageValue={message}
                            onInsertTemplate={(text) => {
                                setMessage(text);
                                onMessageChange?.(text);
                            }}
                        />
                        <Typography variant="caption" sx={{color: '#888'}}>
                            Можно прикрепить до
                            10 {mode === 'image' ? 'фото' : mode === 'file' ? 'файлов' : 'фото или файлов'}
                        </Typography>
                    </Box>
                </Box>
                <MySnackbar {...snack} />
            </Box>
        );
    } else {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" p={2} bgcolor="#f0f0f0">
                <Typography variant="body2" color="textSecondary">Чат уже открыт</Typography>
            </Box>
        );
    }
};
const ChatBlockHeader = ({chat, closeChatHandler, blockedByAnotherAdmin, selectedPatient}) => {
    const {user} = useContext(Context);
    const [balances, setBalances] = useState(null);

    useEffect(() => {
        if (selectedPatient?.id && selectedPatient?.isParent) {
            getAllBalances(selectedPatient.id)
                .then(data => setBalances(data))
                .catch(() => setBalances(null));
        } else {
            setBalances(null);
        }
    }, [selectedPatient?.id, selectedPatient?.isParent]);

    return (
        <Box
            sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#fff",
                borderBottom: "1px solid #ececec",
            }}
        >
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1}}>
                <Stack direction="row" gap={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {chat?.userName || "Без имени"}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={400}>
                        {chat?.userPhone || "Без номера"}
                    </Typography>
                </Stack>

                {
                    chat.adminId !== null && chat.adminId !== user?.user?.id && (
                        <Typography variant="subtitle2">
                            Взял: {chat?.adminName || "Без имени"}
                        </Typography>
                    )
                }
                {
                    ((chat.adminId === user?.user?.id) || (chat.adminId && user.isOwner)) && (
                        <Button onClick={() => closeChatHandler(chat)}>Закрыть чат {blockedByAnotherAdmin && user.isOwner && "(занят)"}</Button>
                    )
                }
            </Box>
            
            {balances && balances.children && balances.children.length > 0 && (
                <Box sx={{mt: 1, p: 1, backgroundColor: "#fff", borderRadius: 1}}>
                    <Typography variant="caption" sx={{fontWeight: 'bold', display: 'block', mb: 0.5}}>
                        Балансы детей:
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        {balances.children.map((child) => (
                            <Typography key={child.id} variant="caption">
                                {child.name || 'Без имени'}: {child.balance} ₽
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    )
}

const ChatBlock = forwardRef(({
                                  messages,
                                  chat,
                                  sendMessage,
                                  openChatHandler,
                                  closeChatHandler,
                                  setMessages,
                                  isLoading,
                                  changingChat,
                                  selectedPatient,
                                  loadMessages,
                                  onSlipOff,
                                  onSlipOn,
                                  onLoadMore,
                                  sendingMessage,
                                  onEditMessage,
                                  messageValue,
                                  onMessageChange,
                                  onInsertTemplate,
                                  availablePlatforms,
                                  selectedPlatform,
                                  onPlatformChange,
                              }, ref) => {
    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);
    const topRef = useRef(null);
    const [fromEmptyState, setFromEmptyState] = useState(true);
    const {user} = useContext(Context);
    const [blockedByAnotherAdmin, setBlockedByAnotherAdmin] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);

    useImperativeHandle(ref, () => ({
        scrollToBottom: () => {
            messagesEndRef.current?.scrollIntoView({behavior: "instant"});
        },
        revertScrollHeight: (prevScrollHeight, prevScrollTop) => {
            const container = containerRef.current;
            const newScrollHeight = container.scrollHeight;
            // восстанавливаем позицию
            container.scrollTop = newScrollHeight - (prevScrollHeight - prevScrollTop);
        },
        getScrollHeight: () => {
            return containerRef.current?.scrollHeight;
        },
        getScrollTop: () => {
            return containerRef.current?.scrollTop;
        }
    }));

    // чтобы скролл внизу при смене чата
    useLayoutEffect(() => {
        if (fromEmptyState) {
            const container = containerRef.current;
            container.scrollTop = container.scrollHeight;
            setFromEmptyState(false);
        }
    }, [messages])

    useEffect(() => {
        setBlockedByAnotherAdmin(chat.adminId !== null && chat.adminId !== user?.user?.id);
    }, [chat, user])

    useEffect(() => {
        if (!topRef.current) return;
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting) {
                    onLoadMore();
                }
            },
            {threshold: 1}
        );
        observer.observe(topRef.current);
        return () => observer.disconnect();
    }, [onLoadMore]);

    useEffect(() => {
        if (messages.length === 0) {
            setFromEmptyState(true);
        }
    }, [messages]);

    useEffect(() => {
        if (!messagesEndRef.current) return;
        onSlipOn();
        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0];
                // если конец сообщений почти виден (например, нижние 90%)
                if (entry.intersectionRatio > 0.1) {
                    onSlipOn();
                } else {
                    onSlipOff();
                }
            },
            {threshold: Array.from({length: 101}, (_, i) => i / 100)} // 0,0.01,...1
        );

        observer.observe(messagesEndRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "row", // делаем две колонки
                border: "1px solid #ccc",
                borderRadius: 2,
                overflow: "hidden",
                marginLeft: "24px",
                maxWidth: "900px",
                width: "100%",
                minWidth: "300px",
            }}
        >
            {/* Левая часть — чат */}
            <Box sx={{flexGrow: 1, display: "flex", flexDirection: "column", width: '100%'}}>
                <ChatBlockHeader chat={chat} closeChatHandler={closeChatHandler} blockedByAnotherAdmin={blockedByAnotherAdmin} selectedPatient={selectedPatient}/>

                <Divider/>

                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: "auto",
                        pt: 1,
                        backgroundColor: "#fafafa",
                        height: "100%",
                        width: "100%",
                        position: "relative",
                    }}
                    ref={containerRef}
                >
                    <div ref={topRef}/>
                    {(() => {
                        const lastReadAt = !chat.isRead && chat.lastReadAt ? new Date(chat.lastReadAt) : null;
                        let separatorIndex = -1;
                        let unreadCount = 0;
                        if (lastReadAt) {
                            for (let i = 0; i < messages.length; i++) {
                                if (new Date(messages[i].datetime) > lastReadAt) {
                                    if (separatorIndex === -1) separatorIndex = i;
                                    unreadCount++;
                                }
                            }
                        }
                        return messages.map((message, index) => {
                            const isPatient = chat.userId === message.fromUserId;
                            const canEdit = !isPatient && message.fromUserId === user?.user?.id;
                            return (
                                <React.Fragment key={message.id}>
                                    {separatorIndex === index && (
                                        <Divider sx={{ my: 1.5 }}>
                                            <Chip
                                                label={`${unreadCount} ${unreadCount === 1 ? 'новое' : 'новых'}`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Divider>
                                    )}
                                    <ChatMessage
                                        isPatient={isPatient}
                                        messageId={message.id}
                                        message={message.message}
                                        media={message.media}
                                        platform={message.platform}
                                        authorName={message["user.internalName"] || message["user.name"]}
                                        datetime={new Date(message.datetime).toLocaleString()}
                                        editedAt={message.editDatetime || null}
                                        canEdit={canEdit}
                                        onEdit={(newText) => onEditMessage && onEditMessage(message.id, newText)}
                                        rowBgColor={separatorIndex !== -1 && index >= separatorIndex
                                            ? "rgba(103, 80, 164, 0.06)"
                                            : undefined}
                                    />
                                </React.Fragment>
                            );
                        });
                    })()}
                    <div ref={messagesEndRef}/>
                </Box>

                <Divider/>

                <ChatBlockBottom
                    chat={chat}
                    sendMessageHandler={sendMessage}
                    openChatHandler={openChatHandler}
                    sendingMessage={sendingMessage}
                    blockedByAnotherAdmin={blockedByAnotherAdmin}
                    messageValue={messageValue}
                    onMessageChange={onMessageChange}
                    onInsertTemplate={onInsertTemplate}
                    availablePlatforms={availablePlatforms}
                    selectedPlatform={selectedPlatform}
                    onPlatformChange={onPlatformChange}
                />
            </Box>

            {/* Оверлей загрузки */}
            {(isLoading || changingChat) && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(255,255,255,1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                        pointerEvents: "all",
                    }}
                >
                    <CircularProgress/>
                </Box>
            )}
        </Box>
    );

});

export default ChatBlock;
